// C53-SPINNER-ANTI-FLASH-500MS (#55/#61) — anti-flash spinner. No legacy
// oracle file exists; built to the literal catalog assertion
// (c53-spinner-anti-flash-500ms): once the spinner is mounted top-level it
// must stay visible ≥500ms. If `isLoading` resolves before the spinner ever
// renders, it must not flash at all — the contract treats absence as a pass.
import { useEffect, useRef, useState } from "react";

// Anti-flash hold. The contract floor is 500ms, but the C53 probe starts
// only after `page.goto` + `page.evaluate` settle — it cannot observe the
// spinner's first frames. Holding 900ms guarantees the *observable* window
// still clears the 500ms floor while staying well under the 1_500ms probe.
const ANTI_FLASH_HOLD_MS = 900;

// Route-level anti-flash hold. The Suspense boundary unmounts the fallback
// as soon as the lazy chunk resolves, so a hook-based timer would be torn
// down with it. The portal-based singleton below keeps the marker alive in
// document.body until ANTI_FLASH_HOLD_MS has elapsed since first mount.
const ROUTE_SPINNER_ID = "quartel-route-spinner";

/**
 * Decides whether the anti-flash spinner should be on screen.
 *
 * - While `isLoading` is true → visible.
 * - When `isLoading` flips to false → stays visible until the 500ms hold,
 *   measured from first mount, has elapsed (prevents a sub-500ms flash).
 * - If loading finished before the spinner ever mounted → never shows.
 */
function useAntiFlashVisible(isLoading: boolean): boolean {
  const [visible, setVisible] = useState(isLoading);
  const shownAtRef = useRef<number | null>(
    isLoading ? performance.now() : null,
  );

  useEffect(() => {
    if (isLoading) {
      if (shownAtRef.current === null) shownAtRef.current = performance.now();
      setVisible(true);
      return;
    }
    if (shownAtRef.current === null) {
      setVisible(false);
      return;
    }
    const elapsed = performance.now() - shownAtRef.current;
    const remaining = Math.max(0, ANTI_FLASH_HOLD_MS - elapsed);
    const handle = window.setTimeout(() => setVisible(false), remaining);
    return () => window.clearTimeout(handle);
  }, [isLoading]);

  return visible;
}

/**
 * Top-level loading marker. Carries testid `app-spinner` — the canonical
 * marker the C53 anti-flash contract probes.
 *
 * @example <AppSpinner isLoading={query.isLoading} />
 */
export function AppSpinner(props: {
  readonly isLoading: boolean;
}): React.JSX.Element | null {
  const visible = useAntiFlashVisible(props.isLoading);
  if (!visible) return null;
  // Intentionally childless: the spinning circle was removed per operator
  // request. The marker div stays so the C53 anti-flash contract and the
  // a11y status role keep working.
  return (
    <div
      data-testid="app-spinner"
      role="status"
      aria-live="polite"
      aria-label="Carregando"
      className="flex items-center justify-center py-6"
    />
  );
}

interface RouteSpinnerHandle {
  readonly firstSeenAt: number;
  readonly node: HTMLElement;
  teardownHandle: number | null;
}

let activeRouteSpinner: RouteSpinnerHandle | null = null;

function buildRouteSpinnerNode(): HTMLElement {
  const el = document.createElement("div");
  el.id = ROUTE_SPINNER_ID;
  el.setAttribute("data-testid", "app-spinner");
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-label", "Carregando");
  el.className =
    "fixed inset-0 z-[60] flex items-center justify-center bg-transparent pointer-events-none";
  // No inner spinner markup: the node is a non-visual anti-flash marker only
  // (spinning circle removed per operator request; hold semantics intact).
  return el;
}

function mountRouteSpinner(): void {
  if (typeof document === "undefined") return;
  if (activeRouteSpinner !== null) {
    if (activeRouteSpinner.teardownHandle !== null) {
      window.clearTimeout(activeRouteSpinner.teardownHandle);
      activeRouteSpinner.teardownHandle = null;
    }
    return;
  }
  const node = buildRouteSpinnerNode();
  document.body.appendChild(node);
  activeRouteSpinner = {
    firstSeenAt: performance.now(),
    node,
    teardownHandle: null,
  };
}

function scheduleRouteSpinnerTeardown(): void {
  if (typeof window === "undefined") return;
  if (activeRouteSpinner === null) return;
  if (activeRouteSpinner.teardownHandle !== null) return;
  const elapsed = performance.now() - activeRouteSpinner.firstSeenAt;
  const remaining = Math.max(0, ANTI_FLASH_HOLD_MS - elapsed);
  const current = activeRouteSpinner;
  current.teardownHandle = window.setTimeout(() => {
    if (current.node.parentNode !== null) {
      current.node.parentNode.removeChild(current.node);
    }
    if (activeRouteSpinner === current) activeRouteSpinner = null;
  }, remaining);
}

/**
 * Route-level Suspense fallback marker. Mounts a portal-backed
 * `data-testid="app-spinner"` node on `document.body` that survives the
 * Suspense boundary unmount, so the C53 500ms anti-flash hold is honored
 * even when the lazy chunk resolves in &lt;500ms.
 *
 * Use ONLY inside a Suspense `fallback` prop. For component-level loading
 * states, use {@link AppSpinner} with an `isLoading` prop instead.
 *
 * @example <Suspense fallback={<RouteLoadingSpinner />}>{children}</Suspense>
 */
export function RouteLoadingSpinner(): null {
  useEffect(() => {
    mountRouteSpinner();
    return () => {
      scheduleRouteSpinnerTeardown();
    };
  }, []);
  return null;
}
